import { Injectable, inject } from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Meta, Title} from '@angular/platform-browser';
import {environment} from '../../environments/environment.development';
import {Breadcrumb} from '../interfaces/Breadcrumb';

export interface SeoPageOptions {
  title: string;
  description?: string;
  /** Относительный или абсолютный путь картинки для og:image */
  image?: string;
  /** Канонический путь страницы без домена, напр. '/news/my-article' */
  path?: string;
  /** og:type: website | article */
  type?: 'website' | 'article';
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private title = inject(Title);
  private meta = inject(Meta);
  private document = inject<Document>(DOCUMENT);


  private readonly jsonLdScriptId = 'seo-json-ld';

  /**
   * Устанавливает title, description, canonical, Open Graph и Twitter Card.
   * Работает и в браузере, и при SSR — теги попадают в отдаваемый HTML.
   */
  setPage(options: SeoPageOptions): void {
    const fullTitle = options.title.includes(environment.websiteName)
      ? options.title
      : `${options.title} — ${environment.websiteName}`;

    this.title.setTitle(fullTitle);

    const description = options.description ?? environment.siteDescription;
    // path приходит и как 'news/slug', и как '/news/slug' — раньше склейка без '/' давала
    // canonical https://smartep.runews/… на ВСЕХ статьях/категориях (вред индексации)
    const url = this.absoluteUrl(options.path ?? '/')!;
    const image = this.absoluteUrl(options.image);

    // standard meta
    this.meta.updateTag({name: 'description', content: description});

    // Open Graph
    this.meta.updateTag({property: 'og:title', content: fullTitle});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:url', content: url});
    this.meta.updateTag({property: 'og:type', content: options.type ?? 'website'});
    this.meta.updateTag({property: 'og:site_name', content: environment.websiteName});
    this.meta.updateTag({property: 'og:locale', content: 'ru_RU'});
    if (image) {
      this.meta.updateTag({property: 'og:image', content: image});
    }

    // Twitter Card
    this.meta.updateTag({name: 'twitter:card', content: image ? 'summary_large_image' : 'summary'});
    this.meta.updateTag({name: 'twitter:title', content: fullTitle});
    this.meta.updateTag({name: 'twitter:description', content: description});
    if (image) {
      this.meta.updateTag({name: 'twitter:image', content: image});
    }

    this.setCanonical(url);
  }

  /**
   * Запрещает индексацию страницы (meta robots noindex).
   * follow: true — ссылки со страницы робот всё равно обходит (оболочка вокруг файла, #1148).
   */
  setNoindex(options: {follow?: boolean} = {}): void {
    this.meta.updateTag({name: 'robots', content: options.follow ? 'noindex, follow' : 'noindex, nofollow'});
  }

  /** Снимает запрет индексации. */
  clearNoindex(): void {
    this.meta.removeTag('name="robots"');
  }

  /** Каноническая ссылка — защита от дублей страниц в индексе. */
  setCanonical(url: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /** Вставляет/заменяет JSON-LD разметку schema.org в <head>. */
  setJsonLd(schema: object | object[]): void {
    this.removeJsonLd();
    const script = this.document.createElement('script');
    script.id = this.jsonLdScriptId;
    script.type = 'application/ld+json';
    // < → \u003c: «</script>» в названии/описании не закроет тег при SSR-сериализации
    script.textContent = JSON.stringify(schema).replace(/</g, '\\u003c');
    this.document.head.appendChild(script);
  }

  removeJsonLd(): void {
    this.document.getElementById(this.jsonLdScriptId)?.remove();
  }

  /** JSON-LD разметка хлебных крошек (BreadcrumbList). */
  setBreadcrumbs(crumbs: Breadcrumb[]): void {
    this.setJsonLd(this.buildBreadcrumbSchema(crumbs));
  }

  /** Собирает объект схемы BreadcrumbList — для комбинирования с другими схемами через setJsonLd([...]). */
  buildBreadcrumbSchema(crumbs: Breadcrumb[]): object {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Главная',
          item: environment.domain + '/',
        },
        ...crumbs.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 2,
          name: c.name,
          ...(c.path ? {item: environment.domain + '/' + c.path} : {}),
        })),
      ],
    };
  }

  /** Убирает HTML-теги и обрезает текст для meta description. */
  static stripHtml(html: string | undefined, maxLength = 160): string {
    if (!html) {
      return '';
    }
    const text = html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&[a-z]+;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length > maxLength ? text.slice(0, maxLength - 1).trimEnd() + '…' : text;
  }

  private absoluteUrl(path?: string): string | undefined {
    if (!path) {
      return undefined;
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    return environment.domain + (path.startsWith('/') ? path : '/' + path);
  }
}
